import { useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatYmd } from '../utils/ukTime';
import { UNKNOWN_CELL } from '../utils/uiText';

/**
 * MonthPicker — a compact, self-contained month selector.
 *
 * Renders a pill `‹  <Month YYYY>  ›` that walks the available months one at a time
 * (skipping any marked `hasData: false`), plus a "Latest" quick-jump to the newest.
 * The order of `months` is caller-defined via `order`, which flips the chevron
 * semantics so the same component works for newest-first or oldest-first lists.
 */
export interface MonthPickerMonth {
  year: number;
  month: number;
  /** Months that have no selectable data are rendered skipped. Default true. */
  hasData?: boolean;
}

export interface MonthPickerProps<T extends MonthPickerMonth = MonthPickerMonth> {
  /** Available months, in the order described by `order`. */
  months: T[];
  /** Currently selected month. When null, defaults to the newest available month. */
  selected?: { year: number; month: number } | null;
  /** Called with the full month record the user chose. */
  onSelect: (month: T) => void;
  /** Render a label for a month. Defaults to "Mon YYYY" (en-GB). */
  formatLabel?: (year: number, month: number) => string;
  /** How `months` is ordered. Default `'newest-first'`. */
  order?: 'newest-first' | 'oldest-first';
  /** Hide the "Latest" quick-jump button. */
  showLatest?: boolean;
  className?: string;
}

const DEFAULT_FORMAT = (year: number, month: number) =>
  formatYmd(`${year}-${String(month).padStart(2, '0')}-01`, { month: 'short', year: 'numeric' });

const hasData = (m: MonthPickerMonth) => m.hasData !== false;

export default function MonthPicker<T extends MonthPickerMonth = MonthPickerMonth>({
  months,
  selected,
  onSelect,
  formatLabel = DEFAULT_FORMAT,
  order = 'newest-first',
  showLatest = true,
  className = '',
}: MonthPickerProps<T>) {
  const count = months.length;

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    return months.findIndex((m) => m.year === selected.year && m.month === selected.month);
  }, [months, selected]);

  const latestIndex = useMemo(() => {
    if (count === 0) return -1;
    // Newest is the first enabled month (newest-first) or the last enabled one (oldest-first).
    if (order === 'oldest-first') {
      for (let i = count - 1; i >= 0; i--) if (hasData(months[i])) return i;
      return -1;
    }
    for (let i = 0; i < count; i++) if (hasData(months[i])) return i;
    return -1;
  }, [months, order, count]);

  const currentIndex = selectedIndex >= 0 ? selectedIndex : Math.max(0, latestIndex);

  // Stepping direction that moves toward OLDER months, in index space.
  const olderStep: 1 | -1 = order === 'newest-first' ? 1 : -1;
  const newerStep: 1 | -1 = order === 'newest-first' ? -1 : 1;

  const step = useCallback(
    (direction: 1 | -1) => {
      if (count === 0) return;
      let idx = currentIndex + direction;
      while (idx >= 0 && idx < count && !hasData(months[idx])) idx += direction;
      if (idx < 0 || idx >= count) return;
      onSelect(months[idx]);
    },
    [count, currentIndex, months, onSelect],
  );

  const hasOlder = useMemo(() => {
    for (let i = currentIndex + olderStep; i >= 0 && i < count; i += olderStep) {
      if (hasData(months[i])) return true;
    }
    return false;
  }, [months, currentIndex, olderStep, count]);

  const hasNewer = useMemo(() => {
    for (let i = currentIndex + newerStep; i >= 0 && i < count; i += newerStep) {
      if (hasData(months[i])) return true;
    }
    return false;
  }, [months, currentIndex, newerStep, count]);

  const goLatest = useCallback(() => {
    if (latestIndex >= 0 && months[latestIndex]) onSelect(months[latestIndex]);
  }, [months, latestIndex, onSelect]);

  const activeMonth = selected ?? months[currentIndex];
  const activeLabel = activeMonth ? formatLabel(activeMonth.year, activeMonth.month) : UNKNOWN_CELL;

  const navButton = (enabled: boolean) =>
    `grid h-7 w-7 place-items-center rounded-full transition-colors ${
      enabled ? 'text-muted-foreground hover:bg-accent hover:text-foreground' : 'cursor-not-allowed text-muted-foreground/40'
    }`;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-0.5 rounded-full border border-border bg-secondary p-1">
        <button
          type="button"
          aria-label="Older month"
          onClick={() => step(olderStep)}
          disabled={!hasOlder}
          className={navButton(hasOlder)}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-22 px-2 text-center text-sm font-semibold text-foreground">{activeLabel}</span>
        <button
          type="button"
          aria-label="Newer month"
          onClick={() => step(newerStep)}
          disabled={!hasNewer}
          className={navButton(hasNewer)}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {showLatest && (
        <button
          type="button"
          onClick={goLatest}
          disabled={!hasNewer}
          className="rounded-full border border-border px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-all hover:border-success/45 hover:text-success disabled:cursor-not-allowed disabled:opacity-40"
        >
          Latest
        </button>
      )}
    </div>
  );
}
